import { createContext, ReactNode, useContext, useMemo, useState, Dispatch, SetStateAction } from "react";
import { ITag } from "../components/PlayerLanding/PlayerSearch/SelectTags";
import { IPlayerSearchResponse } from "../types/IPlayerSearchResponse";
import { IRegionAreaModel, IRegionCompetitionsModel } from "../types/IRegionAreaData";
import { IAttribute, ISection } from "../components/PlayerLanding/PlayerSearch/SelectAttributes";
import { IPositionSearchByZoneResponse } from "../types/IPositionSearchByZoneResponse";
import { ISelectedOption } from "../components/PlayerLanding/PlayerSearch/PlayerFilterSection";
import { ISimilarPlayerCardDetail } from "../types/IPlayerDetail";

interface FilterState {
    zoneChange: boolean;
    checkboxChange: boolean;
    attributeChange: boolean;
    transferValueSliderChange: boolean;
    trueValueSliderChange:boolean;
    contractValueSliderChange: boolean;
    wagesValueSliderChange: boolean;
    ageValueSliderChange: boolean;
    toggleSection: boolean;
    minApperancesChange: boolean;
    statsOnly: boolean;
    footChange: boolean;
    clubLeague: boolean,
    regionChange: boolean,
    search: boolean,
    selectPlayer: boolean,
    profileSelect: boolean,
    positionChange: boolean,
    tagsDataChange: boolean
}

interface IPlayerSearchContextType {
    selectedTags: ITag[];
    handleSelectedTags: (selectedTag: ITag[]) => void;
    handleSelectedPlayer: (players: IPlayerSearchResponse[]) => void;
    selectedPlayers: IPlayerSearchResponse[];
    tagsDrawerOpen: boolean;
    setTagsDrawerOpen: (value: boolean) => void;
    isAllPosition: string;
    setIsAllPosition: (value: string) => void;
    playerSearchResultsVisible: boolean;
    setPlayerSearchResultsVisible: (value: boolean) => void;
    attributesDrawerOpen: boolean;
    setAttributesDrawerOpen: (value: boolean) => void;
    regionAreaData: IRegionAreaModel[];
    handleRegionAreaData: (region: IRegionAreaModel[]) => void;
    clubLeagueData: IRegionCompetitionsModel[];
    handleClubLeagueData: (leaugue: IRegionCompetitionsModel[]) => void;
    selectedAttributesState: IAttribute[];
    handleSelectedAttributes: (selectedAttribute: IAttribute[]) => void;
    foot: number[];
    setFoot: React.Dispatch<React.SetStateAction<number[]>>;
    transferValue: { min: number; max: number };
    setTransferValue: React.Dispatch<React.SetStateAction<{ min: number; max: number }>>;
    trueValue: { min: number; max: number };
    setTrueValue: React.Dispatch<React.SetStateAction<{ min: number; max: number }>>;
    contractUntilValue: { min: number; max: number };
    setContractUntilValue: React.Dispatch<React.SetStateAction<{ min: number; max: number }>>;
    wagesValue: { min: number; max: number };
    setWagesValue: React.Dispatch<React.SetStateAction<{ min: number; max: number }>>;
    ageRange: { min: number; max: number };
    setAgeRange: React.Dispatch<React.SetStateAction<{ min: number; max: number }>>;
    regionValues: ISelectedOption[];
    setRegionValues: React.Dispatch<React.SetStateAction<ISelectedOption[]>>;
    competitionValues: ISelectedOption[];
    setCompetitionValues: React.Dispatch<React.SetStateAction<ISelectedOption[]>>;
    minAppearances: number;
    setMinAppearances: React.Dispatch<React.SetStateAction<number>>;
    startsOnly: boolean;
    setStartsOnly: React.Dispatch<React.SetStateAction<boolean>>;
    selectedProfiles: number[];
    setSelectedProfiles: Dispatch<SetStateAction<number[]>>;
    handleSelectedProfiles: (profileIds: number[]) => void;
    resultSortOrderName: string;
    setResultSortOrderName: (sortValue: string) => void;
    resultSortOrdering: string;
    setResultSortOrdering: (sortOrdering: string) => void;
    isSearchByAttributes: boolean;
    setIsSearchByAttributes: (value: boolean) => void;
    isAltered: boolean;
    setIsAltered: (value: boolean) => void;
    selectedPlayerZones: number[];
    handleSelectedPlayerZones: (zoneIds: number[]) => void;
    selectedPlayerAttributes: ISection[];
    handleSelectedPlayerAttributes: (atrributes: ISection[]) => void;
    isExactMatchesOnly: boolean;
    setIsExactMatchesOnly: (value: boolean) => void;
    isAffordabilityFilter : boolean; 
    setIsAffordabilityFilter:(value: boolean) => void;
    isStylisticFilter : boolean; 
    setIsStylisticFilter : (value: boolean) => void;
    allProfiles: IPositionSearchByZoneResponse[];
    setAllProfiles: React.Dispatch<React.SetStateAction<IPositionSearchByZoneResponse[]>>;
    similarPlayerResultsList: ISimilarPlayerCardDetail;
    setSimilarPlayerResultsList: React.Dispatch<React.SetStateAction<ISimilarPlayerCardDetail | undefined>>;
    originPage: string;
    setOriginPage: React.Dispatch<React.SetStateAction<string>>;
    isPlayerFilterChange: boolean;
    setIsPlayerFilterChange: (value: boolean) => void;
    playerInitialAttributes: ISection[];
    setPlayerInitialAttributes: (playerAttributes: ISection[]) => void;
    reset: () => void;
    currentPlayerPage: number;
    setCurrentPlayerPage: (value: number) => void
    // displayNotification: boolean;
    // setDisplayNotification: (value: boolean) => void;
    filters: FilterState;
    setFilters: Dispatch<SetStateAction<FilterState>>;

    toggleAddButton : boolean;
    setIsToggleAddButton : (value: boolean) => void;
    addedPlayers:any
    setAddedPlayers:any


    handleFilterChange: (filterName: keyof FilterState, filterChange: boolean) => void;
    isButtonDisabled: boolean;
    resetAllFilters: () => void;
    playerSearchOpen : boolean;
    setIsPlayerSearchOpen : (value : boolean) => void;
    comparePlayerImageUrl : string ;
    setComparePlayerImageUrl : (value : string) => void;
    comparePlayerName : string ;
    setComparePlayerName : (value : string) => void;
    comparePlayerId : number ;
    setComparePlayerId : (value : number) => void;
    simulationPlayerSearch : boolean;
    setSimulationPlayerSearch : (value : boolean) => void;
    simulationClubSearch : boolean;
    setSimulationClubSearch : (value : boolean) => void;
}

const SearchPlayerContext = createContext<Partial<IPlayerSearchContextType>>({});

export const usePlayerSearch = (): IPlayerSearchContextType => {
    const context = useContext(SearchPlayerContext);
    if (!context) {
        throw new Error('useSearch must be used within a SearchProvider');
    }
    return context as IPlayerSearchContextType;
};

export const PlayerSearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

    const initialSelectedTags: ITag[] = [];
    const initialSelectedPlayers: IPlayerSearchResponse[] = [];
    const initialTagsDrawerOpen = false;
    const initialIsAllPosition = 'theirposition'; //allpositions';
    const initialPlayerSearchResultsVisible = false;
    const initialAttributesDrawerOpen = false;
    const initialRegionAreaData: IRegionAreaModel[] = [];
    const initialClubLeagueData: IRegionCompetitionsModel[] = [];
    const initialSelectedAttributesState: IAttribute[] = [];
    const initialSelectedProfiles: number[] = [];
    const initialTransferValue = { min: 0, max: 250000000 };
    const initialTrueValue = { min: 0, max: 250000000 };
    const initialContractUntilValue = { min: 0, max: 120 };
    const initialWagesValue = { min: 0, max: 180000000 };
    const initialAgeRange = { min: 16, max: 50 };
    const initialResultSortOrderName = 'Similarity';
    const initialResultSortOrdering = 'DESC';
    const initialIsSearchByAttributes = false;
    const initialIsAltered = false;
    const initialSelectedPlayerZones: number[] = [];
    const initialFoot: number[] = [];
    const initialStartsOnly = false;
    const initialMinAppearances = 0;
    const initialRegionValues: ISelectedOption[] = [];
    const initialCompetitionValues: ISelectedOption[] = [];
    const initialIsExactMatchesOnly = false;
    const initialAllProfiles: IPositionSearchByZoneResponse[] = [];
    const initialSimilarPlayerResultsList: ISimilarPlayerCardDetail | undefined = undefined;
    const initialOriginPage = '';
    const initialIsPlayerFilterChange = false;
    const initialPlayerInitialAttributes: ISection[] = [];

    const [selectedTags, setSelectedTags] = useState<ITag[]>(initialSelectedTags);
    const [selectedPlayers, setSelectedPlayers] = useState<IPlayerSearchResponse[]>(initialSelectedPlayers);
    const [tagsDrawerOpen, setTagsDrawerOpen] = useState<boolean>(initialTagsDrawerOpen);
    const [isAllPosition, setIsAllPosition] = useState<string>(initialIsAllPosition);
    const [playerSearchResultsVisible, setPlayerSearchResultsVisible] = useState<boolean>(initialPlayerSearchResultsVisible);
    const [attributesDrawerOpen, setAttributesDrawerOpen] = useState<boolean>(initialAttributesDrawerOpen);
    const [regionAreaData, setRegionAreaData] = useState<IRegionAreaModel[]>(initialRegionAreaData);
    const [clubLeagueData, setClubLeagueData] = useState<IRegionCompetitionsModel[]>(initialClubLeagueData);
    const [selectedAttributesState, setSelectedAttributesState] = useState<IAttribute[]>(initialSelectedAttributesState);
    const [selectedProfiles, setSelectedProfiles] = useState<number[]>(initialSelectedProfiles);
    const [transferValue, setTransferValue] = useState<{ min: number, max: number }>(initialTransferValue);
    const [trueValue, setTrueValue] = useState<{ min: number, max: number }>(initialTransferValue);
    const [contractUntilValue, setContractUntilValue] = useState<{ min: number, max: number }>(initialContractUntilValue);
    const [wagesValue, setWagesValue] = useState<{ min: number, max: number }>(initialWagesValue);
    const [ageRange, setAgeRange] = useState<{ min: number, max: number }>(initialAgeRange);
    const [resultSortOrderName, setResultSortOrderName] = useState(initialResultSortOrderName);
    const [resultSortOrdering, setResultSortOrdering] = useState(initialResultSortOrdering);
    const [isSearchByAttributes, setIsSearchByAttributes] = useState<boolean>(initialIsSearchByAttributes);
    const [isAltered, setIsAltered] = useState<boolean>(initialIsAltered);
    const [selectedPlayerZones, setSelectedPlayerZones] = useState<number[]>(initialSelectedPlayerZones);
    const [selectedPlayerAttributes, setSelectedPlayerAttributes] = useState<ISection[]>([]);
    const [foot, setFoot] = useState<number[]>(initialFoot);
    const [startsOnly, setStartsOnly] = useState<boolean>(initialStartsOnly);
    const [minAppearances, setMinAppearances] = useState<number>(initialMinAppearances);
    const [regionValues, setRegionValues] = useState<ISelectedOption[]>(initialRegionValues);
    const [competitionValues, setCompetitionValues] = useState<ISelectedOption[]>(initialCompetitionValues);
    const [isExactMatchesOnly, setIsExactMatchesOnly] = useState<boolean>(initialIsExactMatchesOnly);
    const [isAffordabilityFilter, setIsAffordabilityFilter] = useState<boolean>(true);
    const [isStylisticFilter, setIsStylisticFilter] = useState<boolean>(true);
    const [allProfiles, setAllProfiles] = useState<IPositionSearchByZoneResponse[]>(initialAllProfiles);
    const [similarPlayerResultsList, setSimilarPlayerResultsList] = useState<ISimilarPlayerCardDetail | undefined>(initialSimilarPlayerResultsList);
    const [originPage, setOriginPage] = useState(initialOriginPage);
    const [isPlayerFilterChange, setIsPlayerFilterChange] = useState<boolean>(initialIsPlayerFilterChange);
    const [playerInitialAttributes, setPlayerInitialAttributes] = useState<ISection[]>(initialPlayerInitialAttributes);
    const [currentPlayerPage, setCurrentPlayerPage] = useState<number>(1);    
    const [toggleAddButton , setIsToggleAddButton] = useState<boolean>(); 
    const [addedPlayers, setAddedPlayers] = useState<any>({});
    const [playerSearchOpen , setIsPlayerSearchOpen] = useState<boolean>(false)
    const [comparePlayerImageUrl , setComparePlayerImageUrl] = useState<string>('')
    const [comparePlayerName , setComparePlayerName] = useState<string>('')
    const [comparePlayerId , setComparePlayerId] = useState<number>();
    const [simulationPlayerSearch , setSimulationPlayerSearch] = useState<boolean>(false);
    const [simulationClubSearch , setSimulationClubSearch] = useState<boolean>(false);

    const [filters, setFilters] = useState({
        zoneChange: false,
        checkboxChange: false,
        attributeChange: false,
        transferValueSliderChange: false,
        contractValueSliderChange: false,
        wagesValueSliderChange: false,
        ageValueSliderChange: false,
        toggleSection: false,
        minApperancesChange: false,
        statsOnly: false,
        footChange: false,
        clubLeague: false,
        regionChange: false,
        search: false,
        selectPlayer: false,
        profileSelect: false,
        positionChange: false,
        tagsDataChange: false
    });

    const handleFilterChange = (filterName: keyof FilterState, filterChange: boolean) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [filterName]: filterChange
        }));
    };

    const isButtonDisabled = Object.values(filters).some((value) => value === true);

    // console.log(filters)

    // const [displayNotification, setDisplayNotification] = useState<boolean>(false);
    const handleSelectedTags = (selectedTags: ITag[]) => {
        setSelectedTags(selectedTags);
    }
    const handleSelectedPlayer = (players: IPlayerSearchResponse[]) => {
        setSelectedPlayers(players);
    }
    const handleRegionAreaData = (region: IRegionAreaModel[]) => {
        setRegionAreaData(region);
    }
    const handleClubLeagueData = (league: IRegionCompetitionsModel[]) => {
        setClubLeagueData(league);
    }
    const handleSelectedAttributes = (selectedAttribute: IAttribute[]) => {
        setSelectedAttributesState(selectedAttribute);
    }
    const handleSelectedProfiles = (profileIds: number[]) => {
        setSelectedProfiles(profileIds);
    }
    const handleSelectedPlayerZones = (zoneIds: number[]) => {
        setSelectedPlayerZones(zoneIds);
    }
    const handleSelectedPlayerAttributes = (attributes: ISection[]) => {
        setSelectedPlayerAttributes(attributes);
    }

    const reset = () => {
        setSelectedTags(initialSelectedTags);
        //setSelectedPlayers(initialSelectedPlayers);
        setTagsDrawerOpen(initialTagsDrawerOpen);
        setIsAllPosition(initialIsAllPosition);
        setPlayerSearchResultsVisible(initialPlayerSearchResultsVisible);
        setAttributesDrawerOpen(initialAttributesDrawerOpen);
        setRegionAreaData(initialRegionAreaData);
        setClubLeagueData(initialClubLeagueData);
        setSelectedAttributesState(initialSelectedAttributesState);
        setSelectedProfiles(initialSelectedProfiles);
        setTransferValue(initialTransferValue);
        setContractUntilValue(initialContractUntilValue);
        setWagesValue(initialWagesValue);
        setAgeRange(initialAgeRange);
        setResultSortOrderName(initialResultSortOrderName);
        setResultSortOrdering(initialResultSortOrdering);
        setIsSearchByAttributes(initialIsSearchByAttributes);
        setIsAltered(initialIsAltered);
        setSelectedPlayerZones(initialSelectedPlayerZones);
        setFoot(initialFoot);
        setStartsOnly(initialStartsOnly);
        setMinAppearances(initialMinAppearances);
        setRegionValues(initialRegionValues);
        setCompetitionValues(initialCompetitionValues);
        setIsExactMatchesOnly(initialIsExactMatchesOnly);
        setAllProfiles(initialAllProfiles);
        setSimilarPlayerResultsList(initialSimilarPlayerResultsList);
        setOriginPage(initialOriginPage);
        setIsPlayerFilterChange(initialIsPlayerFilterChange);
        setPlayerInitialAttributes(initialPlayerInitialAttributes);
    };

    const resetAllFilters = () => {
        setFilters({
            zoneChange: false,
            checkboxChange: false,
            attributeChange: false,
            transferValueSliderChange: false,
            contractValueSliderChange: false,
            wagesValueSliderChange: false,
            ageValueSliderChange: false,
            toggleSection: false,
            minApperancesChange: false,
            statsOnly: false,
            footChange: false,
            clubLeague: false,
            regionChange: false,
            search: false,
            selectPlayer: false,
            profileSelect: false,
            positionChange: false,
            tagsDataChange: false
        });
    };



    return (
        <SearchPlayerContext.Provider value={{
            selectedTags,
            handleSelectedTags,
            selectedPlayers,
            handleSelectedPlayer,
            tagsDrawerOpen,
            setTagsDrawerOpen,
            isAllPosition,
            setIsAllPosition,
            playerSearchResultsVisible,
            setPlayerSearchResultsVisible,
            attributesDrawerOpen,
            setAttributesDrawerOpen,
            regionAreaData,
            handleRegionAreaData,
            clubLeagueData,
            handleClubLeagueData,
            selectedAttributesState,
            handleSelectedAttributes,
            selectedProfiles,
            setSelectedProfiles,
            handleSelectedProfiles,
            transferValue,
            setTransferValue,
            trueValue,
            setTrueValue,
            contractUntilValue,
            setContractUntilValue,
            wagesValue,
            setWagesValue,
            ageRange,
            setAgeRange,
            resultSortOrderName,
            setResultSortOrderName,
            resultSortOrdering,
            setResultSortOrdering,
            isSearchByAttributes,
            setIsSearchByAttributes,
            isAltered,
            setIsAltered,
            selectedPlayerZones,
            handleSelectedPlayerZones,
            foot,
            setFoot,
            startsOnly,
            setStartsOnly,
            minAppearances,
            setMinAppearances,
            regionValues,
            setRegionValues,
            competitionValues,
            setCompetitionValues,
            isExactMatchesOnly,
            setIsExactMatchesOnly,
            allProfiles,
            setAllProfiles,
            similarPlayerResultsList,
            setSimilarPlayerResultsList,
            originPage,
            setOriginPage,
            isPlayerFilterChange,
            setIsPlayerFilterChange,
            playerInitialAttributes,
            setPlayerInitialAttributes,
            reset,
            // displayNotification,
            // setDisplayNotification
            selectedPlayerAttributes,
            handleSelectedPlayerAttributes,

            currentPlayerPage,
            setCurrentPlayerPage,

            handleFilterChange,
            isButtonDisabled,
            resetAllFilters,

            toggleAddButton,
            setIsToggleAddButton,
            addedPlayers,
            setAddedPlayers,
             playerSearchOpen , setIsPlayerSearchOpen,
             comparePlayerImageUrl , setComparePlayerImageUrl,
             comparePlayerName , setComparePlayerName,
             comparePlayerId , setComparePlayerId,
            isAffordabilityFilter, setIsAffordabilityFilter,
            isStylisticFilter, setIsStylisticFilter,
            simulationPlayerSearch , setSimulationPlayerSearch,
            simulationClubSearch , setSimulationClubSearch
        }}>
            {children}
        </SearchPlayerContext.Provider>
    );
}